import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { bindActionCreators } from 'redux';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedRecommendationsSpace } from './ConnectedRecommendationsSpace';

export interface RecommendationsSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
}

export interface RecommendationsSpaceProviderStateProps {
  readonly isVizRecommendationsEnabled: boolean;
}

export interface RecommendationsSpaceProviderDispatchProps {
  readonly requestSpaceContents: () => void;
}

export interface RecommendationsSpaceProviderPropsFromAngular {
  readonly browserTitleService: BrowserTitleService;
  readonly telemetry: Telemetry;
}

export type RecommendationsSpaceProviderNonAngularProps = RecommendationsSpaceProviderOwnProps & RecommendationsSpaceProviderStateProps & RecommendationsSpaceProviderDispatchProps;
export type RecommendationsSpaceProviderProps = RecommendationsSpaceProviderNonAngularProps & RecommendationsSpaceProviderPropsFromAngular;

export class RecommendationsSpaceProviderComponent extends React.PureComponent<RecommendationsSpaceProviderProps> {

  public render() {
    if (this.props.isVizRecommendationsEnabled) {
      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'recommendations',
        subspaceName: undefined,
      };

      return (
        <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
          <ConnectedRecommendationsSpace telemetry={this.props.telemetry} angularContext={this.props.angularContext}/>
        </ConnectedSpaceInfoProvider>
      );
    } else {
      return <RouterError errorType={'default'} urls={this.props.urls}/>;
    }
  }

  public componentDidMount() {
    if (this.props.isVizRecommendationsEnabled) {
      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.vizRecommendationsTitle() });
      this.props.requestSpaceContents();
    }
  }

  public componentDidUpdate(prevProps: RecommendationsSpaceProviderProps) {
    if (this.props.isVizRecommendationsEnabled &&
      !_.isUndefined(this.props.siteName) &&
      prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }
}

export namespace RecommendationsSpaceProviderAngularProvider {

  function getAngularServicesWithServiceLocator() {
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    return {
      telemetry,
      browserTitleService,
    };
  }

  function selectPropsFromAngularServices(angularServices: RecommendationsSpaceProviderPropsFromAngular) {
    return {
      telemetry: angularServices.telemetry,
      browserTitleService: angularServices.browserTitleService,
    };
  }

  export const HOC = angularProviderHOC<RecommendationsSpaceProviderNonAngularProps, RecommendationsSpaceProviderPropsFromAngular, RecommendationsSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const RecommendationsSpaceProviderAng: React.ComponentType<RecommendationsSpaceProviderNonAngularProps>
  = RecommendationsSpaceProviderAngularProvider.HOC(RecommendationsSpaceProviderComponent);

const mapStateToProps: MapStateToProps<RecommendationsSpaceProviderStateProps> = (state: AppState) => ({
  isVizRecommendationsEnabled: SessionSiteSelectors.isVizRecommendationsEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<RecommendationsSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
}, dispatch);

export const RecommendationsSpaceProvider: React.ComponentClass<RecommendationsSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(RecommendationsSpaceProviderAng);
